import React, { useState, useCallback, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { openOrganizationModal } from "../../js/actions";
import { useIsMobile } from "../../hooks/useMediaQuery";
import Loader from "../global/Loader";
import { SwitchArrowsIcon } from "./SvgIcons";
import { NAV_LINKS } from "../../js/constants";
import {
	DashboardIcon,
	ReservationsIcon,
	CalendarIcon,
	CleaningIcon,
	HouseIcon,
	CollectionsIcon,
	WebsiteIcon,
	TaxIcon,
	OrganizationIcon,
	UsersIcon,
	PromotionsIcon,
	AiChatIcon,
	HeadSetIcon,
	SpreadsheetIcon,
	ChartIcon,
	PercentIcon,
	ReportsIcon,
	LogoutIcon,
	CalendarCrossIcon,
	CreditCardIcon,
	ValidationIcon,
	InfoIcon,
	FaqIcon,
} from "./SvgIcons";

const NAV_ICON_MAP = {
	"icon-dashboard": DashboardIcon,
	"icon-reservations": ReservationsIcon,
	"icon-calendar": CalendarIcon,
	"icon-cleaning": CleaningIcon,
	"icon-house": HouseIcon,
	"icon-collections": CollectionsIcon,
	"icon-website-icon": WebsiteIcon,
	"icon-tax": TaxIcon,
	"icon-organization": OrganizationIcon,
	"icon-users": UsersIcon,
	"icon-promotions": PromotionsIcon,
	"icon-ai-chat": AiChatIcon,
	"icon-head-set": HeadSetIcon,
	"icon-spreadsheet": SpreadsheetIcon,
	"icon-chart": ChartIcon,
	"icon-percent": PercentIcon,
	"icon-reports": ReportsIcon,
	"icon-logout": LogoutIcon,
	"icon-act-fast": CalendarCrossIcon,
	"icon-credit-card": CreditCardIcon,
	"icon-validation": ValidationIcon,
	"icon-info": InfoIcon,
	"icon-faq": FaqIcon,
};

const ALLOWED_ORGS = ["_global_", "propertymanagers", "propertyowners"];

// Tabs that live under the Dashboard in the tab bar but don't have their own
// side nav entry, so the Dashboard icon should stay highlighted for them too.
const DASHBOARD_TAB_PATHS = ["/messages", "/opportunities", "/comm-management", "/reviews"];

export function SideNav({ onLogout }) {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	const permissions = useSelector((s) => s.authState.permissions ?? []);
	const orgName = useSelector((s) => s.roleManager.org);
	const orgTitle = useSelector((s) => s.roleManager.orgTitle);
	const organizations = useSelector(
		(s) => s.authState.user.organization_role_tree ?? [],
	);
	const unsavedData = useSelector((s) => s.updateUnsavedData.unsavedData);

	const [isOpen, setIsOpen] = useState(false);
	const [isLoading] = useState(false);
	const [tooltip, setTooltip] = useState(null);
	const isMobile = useIsMobile();

	// ── Sidebar state ─────────────────────────────────────────────────────────

	const toggleSideBar = useCallback(() => setIsOpen((prev) => !prev), []);

	useEffect(() => {
		document.body.classList.toggle("sidebar-open", isOpen);
		return () => document.body.classList.remove("sidebar-open");
	}, [isOpen]);

	useEffect(() => {
		const handleExternalToggle = () => toggleSideBar();
		window.addEventListener("sidebar-toggle", handleExternalToggle);
		return () => window.removeEventListener("sidebar-toggle", handleExternalToggle);
	}, [toggleSideBar]);

	// Close the mobile overlay sidebar once an organization switch completes
	useEffect(() => {
		const handleOrgSwitched = () => {
			if (isMobile) setIsOpen(false);
		};
		window.addEventListener("organization-switched", handleOrgSwitched);
		return () =>
			window.removeEventListener("organization-switched", handleOrgSwitched);
	}, [isMobile]);

	// ── Permissions ───────────────────────────────────────────────────────────

	const checkPermissions = useCallback(
		(required, mode = "any") => {
			if (!required || required.length === 0) return true;
			if (mode === "all") return required.every((p) => permissions.includes(p));
			return required.some((p) => permissions.includes(p));
		},
		[permissions],
	);

	const isAllowedOrganization = useCallback(() => {
		return organizations.some((org) =>
			ALLOWED_ORGS.includes(org.organization_name),
		);
	}, [organizations]);

	const hasAccessToSection = useCallback(
		(links) => {
			return links.some((link) => {
				if (!checkPermissions(link.required, link.mode)) return false;
				const isOrgOrUser =
					link.path === "/account-management/organizations" ||
					link.path === "/account-management/users";
				if (isOrgOrUser && !isAllowedOrganization()) return false;
				return true;
			});
		},
		[checkPermissions, isAllowedOrganization],
	);

	// ── Tooltip ───────────────────────────────────────────────────────────────

	const showTooltip = useCallback(
		(e, text) => {
			if (isOpen) return;
			const rect = e.currentTarget.getBoundingClientRect();
			setTooltip({
				text,
				top: rect.top + rect.height / 2,
			});
		},
		[isOpen],
	);

	const hideTooltip = useCallback(() => setTooltip(null), []);

	// ── Actions ───────────────────────────────────────────────────────────────

	const logout = useCallback(
		async (e) => {
			e.preventDefault();
			if (unsavedData) {
				navigate("/");
			} else {
				try {
					await onLogout();
					navigate("/");
				} catch (err) {
					console.error(err);
				}
			}
			if (typeof sessionStorage !== "undefined") {
				Object.keys(sessionStorage).forEach((key) => {
					if (key !== "org" && key !== "orgId" && key !== "orgTitle") {
						sessionStorage.removeItem(key);
					}
				});
			}
		},
		[unsavedData, onLogout, navigate],
	);

	const changeOrg = useCallback(() => {
		sessionStorage.removeItem("reservations-listing");
		sessionStorage.removeItem("properties-listing");
		dispatch(openOrganizationModal("change-org-modal"));
	}, [dispatch]);

	// ── Nav link renderer ─────────────────────────────────────────────────────

	const renderNavLinks = (links = NAV_LINKS.main) => {
		return links.map((link, id) => {
			if (!checkPermissions(link.required, link.mode)) return null;

			const isOrgOrUser =
				link.path === "/account-management/organizations" ||
				link.path === "/account-management/users";
			if (isOrgOrUser && !isAllowedOrganization()) return null;

			const SvgIcon = NAV_ICON_MAP[link.icon];

			const isDashboardLink = link.path === "/dashboard";
			const isDashboardTabRoute =
				isDashboardLink &&
				DASHBOARD_TAB_PATHS.some(
					(p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
				);

			return (
				<li key={id}>
					<NavLink
						to={link.path}
						data-tooltip={link.name}
						onMouseEnter={(e) => showTooltip(e, link.name)}
						onMouseLeave={hideTooltip}
						className={
							isDashboardLink
								? ({ isActive }) => (isActive || isDashboardTabRoute ? "active" : "")
								: undefined
						}>
						{link.emoji
							? <span className="nav-emoji">{link.emoji}</span>
							: SvgIcon ? <SvgIcon /> : <i className={link.icon} />
						}
						<span>{link.name}</span>
						{link.badge && <sup>{link.badge}</sup>}
					</NavLink>
				</li>
			);
		});
	};

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div className="side-nav">
			{isLoading && <Loader />}
			{isOpen && <div className="sidebar-backdrop" onClick={toggleSideBar} aria-hidden="true" />}
			<div className={`sidebar ${isOpen ? "expand" : "collapsed"}`}>
				<div className="sidebar-wrap">
					<div className={`nav-link-container ${isOpen ? "open" : ""}`}>
						<div className="org-wrap">
							{isOpen ? (
								<div className="org-card" role="button" onClick={changeOrg}>
									<div className="org-card-content">
										<p className="org-label">Account</p>
										<span className="org-name">{orgTitle || orgName}</span>
									</div>
									<span className="org-switch-icon">
										<SwitchArrowsIcon size={20} />
									</span>
								</div>
							) : (
								<span
									className="user-profile"
									role="button"
									onClick={changeOrg}
									onMouseEnter={(e) => showTooltip(e, "Change Account")}
									onMouseLeave={hideTooltip}>
									<SwitchArrowsIcon size={16} />
								</span>
							)}
						</div>

						<ul className="list-unstyled components">
							{hasAccessToSection(NAV_LINKS.accountManagement) && (
								<>
									<li className="nav-section-header">ACCOUNT MANAGEMENT</li>
									{renderNavLinks(NAV_LINKS.accountManagement)}
								</>
							)}

							{hasAccessToSection(
								NAV_LINKS.main.filter((l) => l.section === "OPERATIONS"),
							) && <li className="nav-section-header">OPERATIONS</li>}
							{renderNavLinks(
								NAV_LINKS.main.filter((l) => l.section === "OPERATIONS"),
							)}

							{hasAccessToSection(
								NAV_LINKS.main.filter((l) => l.section === "MANAGEMENT"),
							) && <li className="nav-section-header">MANAGEMENT</li>}
							{renderNavLinks(
								NAV_LINKS.main.filter((l) => l.section === "MANAGEMENT"),
							)}

							{hasAccessToSection(NAV_LINKS.reports) && (
								<>
									<li className="nav-section-header">ANALYTICS</li>
									<li>
										<NavLink
											to="/reports"
											data-tooltip="Reports"
											onMouseEnter={(e) => showTooltip(e, "Reports")}
											onMouseLeave={hideTooltip}>
											<ReportsIcon />
											<span>Reports</span>
										</NavLink>
									</li>
								</>
							)}
						</ul>
					</div>

					{/* <div className="sidebar-footer">
						<NavLink
							to="/"
							className="logout-active"
							data-tooltip="Sign Out"
							onMouseEnter={(e) => showTooltip(e, "Sign Out")}
							onMouseLeave={hideTooltip}
							onClick={logout}>
							<LogoutIcon />
							<span>Sign Out</span>
						</NavLink>
					</div> */}
				</div>
			</div>

			{tooltip && (
				<div
					className="sidenav-tooltip"
					style={{ top: tooltip.top, left: tooltip.left }}>
					{tooltip.text}
				</div>
			)}
		</div>
	);
}

export default SideNav;
